<template>
  <div class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg relative transition-shadow duration-300 p-6 space-y-6">
    <div class="flex items-start justify-between">
      <div class="space-y-6 w-full">
        <Button
            class="hover-lift-no-shadow text-left"
            variant="ghost-no-padding"
            @click="redirectToChallenge(challenge)"
        >
          <Icon name="arrow_back_ios" class="w-5 h-5  text-xl text-zinc-800" />
          <span class="ml-1.5 text-sm text-zinc-800">{{ __('Back to Challenge') }}</span>
        </Button>

        <div class="flex items-center gap-4">
          <img
              class="h-11 w-11 rounded-full ring-2 ring-gray-200"
              :src="submission.user.profile_photo"
              :alt="submission.user.full_name"
          />
          <div>
            <span class="text-3xl font-medium text-zinc-800">
              {{ submission.user.full_name }}
            </span>
          </div>
        </div>

        <div class="flex gap-3 flex-col sm:flex-row">
          <div class="flex w-auto md:w-1/2 flex-row items-center gap-3">
            <div class="w-1/2 sm:w-auto">
              <Button
                  variant="outline"
                  @click="openSendMessage"
                  class="flex items-center justify-center gap-2 whitespace-nowrap w-full"
              >
                <Icon name="send" class="w-5 h-5 text-primary text-xl flex-shrink-0" />
                <span class="text-primary text-sm">{{ __('Send Message') }}</span>
              </Button>
            </div>
            <div class="w-1/2 sm:w-auto">
              <AddToPopup
                  :options="availablePipelines"
                  :is-loading="isLoadingPipeline"
                  :show-icon="true"
                  :is-open="activePopup==='pipeline'"
                  popup-position="top"
                  @select-option="handleAddToPipeline"
                  @toggle="handlePopupToggle('pipeline')"
                  :button-text="__('Add to Pipeline')"
              />
            </div>
          </div>
          <div class="flex w-auto md:w-1/2 flex-row items-center">
            <AddToPopup
                :options="availableJobs"
                :is-loading="isLoadingJob"
                :show-icon="true"
                popup-position="top"
                :is-open="activePopup==='job'"
                @select-option="handleInviteToJob"
                @toggle="handlePopupToggle('job')"
                :button-text="__('Invite to Job')"
                class="w-full sm:w-auto items-center justify-center"
            />
          </div>
        </div>

        <div class="flex md:hidden">
          <div class="flex flex-row items-center justify-center w-full text-center px-3 py-2 text-gray-600 rounded-xl border-2 border-gray-200 text-2xl font-medium gap-2">
            <div>{{ totalPoints }}</div>
            <div>{{ __('Points') }}</div>
          </div>
        </div>

        <div class="flex w-full sm:hidden">
          <button
              class="flex-1 text-center py-2 font-medium border transition-colors duration-150 rounded-l-lg"
              :class="mobileTab === 'details'
              ? 'bg-secondary text-white border-transparent'
              : 'bg-white text-zinc-600 border-zinc-300'"
              @click="mobileTab = 'details'"
          >
            <span class="text-sm">{{ __('Candidate Info') }}</span>
          </button>

          <button
              class="flex-1 text-center py-2 font-medium border transition-colors duration-150 rounded-r-lg"
              :class="mobileTab === 'candidates'
              ? 'bg-secondary text-white border-transparent'
              : 'bg-white text-zinc-600 border-zinc-300'"
              @click="mobileTab = 'candidates'"
          >
            <span class="text-sm">{{ __('Challenge') }}</span>
          </button>
        </div>

        <div v-if="mobileTab === 'details'" class="sm:hidden space-y-4">
          <div class="flex flex-col gap-2">
            <div class="text-sm text-zinc-800">
              <span>{{ __('Challenge:') }}</span>
              <span class="font-medium">{{ challenge.title }}</span>
            </div>

            <div class="text-sm text-zinc-800">
              <span>{{ __('Submitted:') }}</span>
              <span class="font-medium">{{ formattedSubmissionDate }}</span>
            </div>
          </div>

          <div v-if="hasExistingNote && !isEditMode" class="relative text-sm text-gray-600 rounded-xl border-zinc-400 border p-4">
            <button
                @click="enableEditMode"
                class="absolute top-4 right-6 p-1 rounded transition"
                type="button"
            >
              <Icon name="edit" class="w-3 h-3 text-primary" is-filled/>
            </button>
            <div class="flex items-center gap-4">
              <img
                  class="h-11 w-11 rounded-full ring-2 ring-gray-200 self-start"
                  :src="recruiter.profile_photo"
                  :alt="recruiter.full_name"
              />
              <div class="flex flex-col items-start gap-1">
                <span class="text-xs text-zinc-800 font-medium">
                  {{ recruiter.full_name }}
                </span>
                <span class="text-base text-zinc-600 pr-4">
                  {{ savedNote }}
                </span>
              </div>
            </div>
          </div>

          <ReviewTextarea
              v-if="!hasExistingNote || isEditMode"
              id="general_feedback"
              :value="localNote"
              :placeholder="__('Add your review notes...')"
              @update:value="val => localNote = val"
              @save="saveGeneralFeedback"
          />
        </div>

        <div class="hidden sm:flex items-center gap-x-2">
          <div class="text-sm text-zinc-800">
            <span>{{ __('Challenge:') }}</span>
            <span class="font-medium">{{ challenge.title }}</span>
          </div>

          <div class="w-0.5 h-4 bg-zinc-600 self-center" aria-hidden="true"></div>

          <div class="text-sm text-zinc-800">
            <span>{{ __('Submitted:') }}</span>
            <span class="font-medium">{{ formattedSubmissionDate }}</span>
          </div>
        </div>
      </div>

      <div class="hidden md:flex items-center gap-4">
        <div class="flex flex-col justify-center items-center w-36 h-36 rounded-xl border-2 border-zinc-200">
          <div class="text-4xl font-bold text-zinc-800">{{ totalPoints }}</div>
          <div class="text-sm text-gray-600">{{ __('Points') }}</div>
        </div>
      </div>
    </div>

    <div v-if="hasExistingNote && !isEditMode" class="hidden sm:block relative text-sm text-gray-600 rounded-xl border-zinc-400 border p-4">
      <button
          @click="enableEditMode"
          class="absolute top-4 right-6 p-1 rounded transition"
          type="button"
      >
        <Icon name="edit" class="w-3 h-3 text-primary" is-filled/>
      </button>
      <div class="flex items-center gap-4">
        <img
            class="h-11 w-11 rounded-full ring-2 ring-gray-200 self-start"
            :src="recruiter.profile_photo"
            :alt="recruiter.full_name"
        />
        <div class="flex flex-col items-start gap-1">
          <span class="text-xs text-zinc-800 font-medium">
            {{ recruiter.full_name }}
          </span>
          <span class="text-base text-zinc-600 pr-4">
            {{ savedNote }}
          </span>
        </div>
      </div>
    </div>

    <ReviewTextarea
        v-if="(!hasExistingNote || isEditMode)"
        class="hidden sm:block"
        id="general_feedback"
        :value="localNote"
        :placeholder="__('Add your review notes...')"
        @update:value="val => localNote = val"
        @save="saveGeneralFeedback"
    />
  </div>

  <div v-if="mobileTab === 'candidates' || true" :class="{ 'sm:block': true, 'hidden': mobileTab !== 'candidates' }">
    <div v-if="allReviewed" class="mt-4 bg-success/10 rounded-2xl items-center justify-center text-center py-2.5">
      <span class="text-xl md:text-2xl font-medium text-zinc-800">{{ __('All questions reviewed') }}</span>
    </div>

    <div class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 p-6 space-y-6 mt-4">
      <div class="mb-4">
        <ChallengeProgressBar
            :current="currentQuestionIndex + 1"
            :total="totalQuestions"
        />
      </div>

      <ReviewQuestionCard
          v-if="currentQuestion"
          :question-data="currentQuestion"
          :question-number="currentQuestionIndex + 1"
          :challenge-id="challenge.id"
          :submission-id="submission.id"
          :recruiter="recruiter"
          :is-first-question="currentQuestionIndex === 0"
          :is-last-question="currentQuestionIndex === totalQuestions - 1"
          :current-score="userScores[currentQuestion.question.id]"
          :all-reviewed="allReviewed"
          @next="nextQuestion"
          @previous="previousQuestion"
          @complete="handleComplete"
          @score-saved="handleScoreUpdate"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted, watch } from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { Button } from '@/components/block/button';
import { Icon } from '@/components/block/icon';
import { __ } from '@/helpers';
import ChallengeProgressBar from "@/pages/candidate/challenges/components/ChallengeProgressBar.vue";
import ReviewQuestionCard from "@/pages/company/challenges/components/ReviewQuestionCard.vue";
import ReviewTextarea from "@/pages/company/challenges/components/ReviewTextarea.vue";
import AddToPopup from "@/pages/company/challenges/components/AddToPopup.vue";

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  availablePipelines: {
    type: Array,
    default: () => []
  },
  availableJobs: {
    type: Array,
    default: () => []
  },
});

const toast = useToast();
const currentQuestionIndex = ref(0);
const reviewPoints = reactive({});
const reviewFeedback = reactive({});

const challenge = computed(() => props.data.challenge);
const submission = computed(() => props.data.submission);
const questions = computed(() => props.data.questions);
const recruiter = computed(() => props.data.recruiter);

const localNote = ref('');
const savedNote = ref('');
const isEditMode = ref(false);

const userScores = reactive({});

const totalQuestions = computed(() => questions.value?.length || 0);
const currentQuestion = computed(() => questions.value?.[currentQuestionIndex.value]);

const isLoadingJob = ref(false);
const isLoadingPipeline = ref(false);

const hasExistingNote = computed(() => {
  return savedNote.value && savedNote.value.trim() !== '';
});

const activePopup = ref(null);
const mobileTab = ref('details');

const enableEditMode = () => {
  isEditMode.value = true;
  localNote.value = savedNote.value;
};

const initializeReviewData = () => {
  if (submission.value.general_feedback) {
    savedNote.value = submission.value.general_feedback;
    localNote.value = '';
  }

  if (questions.value && questions.value.length > 0) {
    questions.value.forEach(q => {
      if (q.user_answer) {
        userScores[q.user_answer.id] = q.user_answer.score;
        reviewPoints[q.user_answer.id] = q.user_answer.score || 0;
        reviewFeedback[q.user_answer.id] = q.user_answer.review_notes || '';
      }
    });
  }
};

const totalPoints = computed(() => {
  let total = 0;
  if (questions.value && questions.value.length > 0) {
    questions.value.forEach(q => {
      if (q.question.answer_type === 1 || q.question.answer_type === 2) {
        total += q.user_answer?.score || 0;
      } else {
        const trackedScore = userScores[q.question.id];
        if (trackedScore !== null && trackedScore !== undefined) {
          total += trackedScore;
        } else {
          total += q.user_answer?.score || 0;
        }
      }
    });
  }
  return total;
});

const allReviewed = computed(() => {
  return questions.value.filter(q => q.question.answer_type === 3).every(q => {
    const score = q.user_answer.score;
    return score !== null && score !== undefined;
  })
});

const formattedSubmissionDate = computed(() => {
  const d = new Date(submission.value.submitted_at);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return ` ${dd}.${mm}.${yyyy}`;
});

const nextQuestion = () => {
  if (currentQuestionIndex.value < totalQuestions.value - 1) {
    currentQuestionIndex.value++;
  }
};

const previousQuestion = () => {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--;
  }
};

const redirectToChallenge = (challenge) => {
  window.location.href = route('redesign.company.challenges.show', { challenge: challenge.id});
};

const openSendMessage = () => {
  if (submission.value.user.message_url) {
    window.open(submission.value.user.message_url, '_blank');
  } else {
    toast.error(__('Unable to send message'));
  }
};

const handleAddToPipeline = async (pipeline) => {
  activePopup.value = null;
  isLoadingPipeline.value = true;
  try {
    const response = await axios.post(route('redesign.company.challenges.add-to-pipeline'), {
      pipeline_id: pipeline.id,
      user_id: submission.value.user.id,
    });

    if (response.data.success) {
      toast.success(response.data.message);
    } else {
      toast.error(response.data.message);
    }
  } catch (error) {
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else if (error.response?.status === 500) {
      toast.error(__('Something went wrong. Please try again.'));
    } else {
      toast.error(__('Failed to add to pipeline'));
    }
  } finally {
    isLoadingPipeline.value = false;
  }
};

const handleInviteToJob = async (job) => {
  activePopup.value = null;
  isLoadingJob.value = true;

  try {
      const response = await axios.post(route('redesign.company.challenges.invite-to-job'), {
        job_id: job.id,
        user_id: submission.value.user.id,
      });

      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
  } catch (error) {
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error(__('Failed to send invite'));
    }
  } finally {
    isLoadingJob.value = false;
  }
};

const handleScoreUpdate = (questionId, score) => {
  userScores[questionId] = score;
}

const handlePopupToggle = (popupName) => {
  if (activePopup.value === popupName) {
    activePopup.value = null;
  } else {
    activePopup.value = popupName;
  }
}

const handleComplete = async () => {
  try {
    await axios.post(
        route('redesign.company.challenges.complete-review', {
          challenge: challenge.value.id,
          submission: submission.value.id
        })
    );

    toast.success(__('Review completed successfully'));
    redirectToChallenge(challenge.value);
  } catch (error) {
    toast.error(__('Failed to complete review'));
  }
};

const saveGeneralFeedback = async (noteText) => {
  try {
    const response = await axios.post(
        route('redesign.company.challenges.general-feedback', {
          challenge: challenge.value.id,
          submission: submission.value.id,
        }),
        { general_feedback: noteText }
    );

    if (response.data.success) {
      savedNote.value = noteText;
      localNote.value = noteText;
      isEditMode.value = false;
      toast.success(response.data.message);
    }
  } catch (error) {
    toast.error(__('Failed to save note'));
  }
};
onMounted(() => {
  initializeReviewData();
});

watch(questions, (newQuestions) => {
  if (newQuestions && newQuestions.length > 0) {
    initializeReviewData();
  }
}, { immediate: true });

</script>